// Taşeron portalı — Belge Analizi gelen kutusu: çıkarılan kişi ile personel listesi arasında
// eşleştirme (plan S25 C8, `docs/plans/S25-analysis-inbox.md`; A6b — çapraz-kiracı sorgu YOK,
// tamamen istemcide, sayfanın ZATEN çektiği kapsamlı personel listesine karşı).
//
// **Eşleştirme kuralı — TC ÖNCE, ad YEDEK (yalnız TC hiç kullanılamıyorsa).**
//   1) TC: `subject.tcKimlikNo` (yalnız sağlama toplamı geçerliyken dolu) VARSA o kullanılır;
//      YOKSA taranan kalemlerin (`items`) `fields.tcKimlikNo`sundan `parse === 'ok'` olan İLK
//      değerin (`normalized ?? raw`) kullanılır. TC bulunduysa AD KARŞILAŞTIRMASI YAPILMAZ — TC
//      güçlü sinyaldir, tek başına karara yeter.
//   2) Kullanılabilir TC YOKSA `subject.fullName`e düşülür — token kümesi kesişimi (en az bir
//      ortak token, `nameTokens`) eşleşme sayılır.
//   3) Ne TC ne ad varsa (`subject === null` ya da ikisi de boş) → eşleşme YOK sayılır (KAYIT
//      DIŞI): karşılaştıracak hiçbir sinyal olmadığında güvenli varsayılan, sessizce "kayıtlı"
//      saymak DEĞİL, işareti göstermek ve kullanıcıya bıraktırmaktır.
//
// **Aynı `extractedIdentity` iki farklı kapsamda çağrılır** (kasıtlı, TEK fonksiyon):
//   - KAYIT DIŞI etiketi (rail 2. seviye + inceleme paneli alt başlığı) → `group.items`
//     (GRUBUN TÜMÜ) taranır: `kind === 'subject'` grubunda tüm kalemler ZATEN aynı kişiye
//     ait (`item.subjectId` ile gruplama, `lib/analysisInboxTree.ts`) — aggregation GÜVENLİDİR.
//   - Uyuşmazlık uyarısı (seçili kişi ↔ BU belge, AC8) → yalnız `[item]` (TEK kalem) taranır:
//     `Kişi belirlenemedi` grubunda `group.items` FARKLI kişilere ait olabilir (yalnız
//     eşleşmedikleri için aynı kovadalar) — grubun TÜMÜNÜ taramak yanlış kişinin alanını
//     karışıma sokardı. "Bu belge ne diyor" sorusu yalnız TEK kalemin alanlarıyla cevaplanır.
//
// `tcKimlikNo`/`fullName` GÜVENİLMEZ metindir (S17-D8/KVKK) — yalnız düz React child olarak
// render edilir, URL'ye/loga/`title`'a asla girmez.

import type {
  ContractorPersonnelDto,
  DocumentAnalysisItemDto,
  DocumentAnalysisSubjectDto,
} from '@isgportal/shared';
import { nameTokens, namesConflict } from './nameMatch';

export interface ExtractedIdentity {
  tcKimlikNo: string | null;
  fullName: string | null;
}

function fieldTcFrom(items: DocumentAnalysisItemDto[]): string | null {
  for (const item of items) {
    const field = item.fields.tcKimlikNo;
    if (field && field.parse === 'ok') {
      return field.normalized ?? field.raw;
    }
  }
  return null;
}

/** Belge(ler)den çıkarılan kimlik — bkz. dosya üstü not (TC-önce, ad-yedek). */
export function extractedIdentity(
  subject: DocumentAnalysisSubjectDto | null,
  items: DocumentAnalysisItemDto[],
): ExtractedIdentity {
  return {
    tcKimlikNo: subject?.tcKimlikNo ?? fieldTcFrom(items),
    fullName: subject?.fullName ?? null,
  };
}

/**
 * Çıkarılan kimlik, VERİLEN listede kayıtlı mı (KAYIT DIŞI kararı, AC7)? TC varsa YALNIZ TC
 * karşılaştırılır (ad'a bakılmaz); yoksa ad token kesişimine düşülür; ikisi de yoksa `false`
 * (kayıt dışı sayılır — bkz. dosya üstü not §3).
 *
 * ⚠️ Çağıran `personnel` listesini KENDİSİ süzer — bu fonksiyon `kind`e bakmaz (S25 web kod
 * incelemesi #2). `personnelRegistrationStatus` bu ayrımı (aktive personel / bekleyen davet)
 * doğru sırayla uygulayan TEK giriş noktasıdır; iki çağıran (`AnalysisInboxRail`,
 * `AnalysisReviewPane`) doğrudan BUNU kullanır, bu fonksiyonu ayrı ayrı ÇAĞIRMAZ.
 */
export function isRegisteredPersonnel(
  identity: ExtractedIdentity,
  personnel: ContractorPersonnelDto[],
): boolean {
  if (identity.tcKimlikNo) {
    return personnel.some((p) => p.tcKimlikNo === identity.tcKimlikNo);
  }
  if (identity.fullName) {
    const tokens = nameTokens(identity.fullName);
    if (tokens.size === 0) {
      return false;
    }
    return personnel.some((p) => {
      const personTokens = nameTokens(`${p.firstName} ${p.lastName}`);
      for (const token of tokens) {
        if (personTokens.has(token)) {
          return true;
        }
      }
      return false;
    });
  }
  return false;
}

export type PersonnelRegistrationStatus = 'registered' | 'invited' | 'unregistered';

/**
 * KAYIT DIŞI kararının TEK giriş noktası (S25 web kod incelemesi #2 düzeltmesi). Eskiden her
 * çağıran `isRegisteredPersonnel`i SÜZÜLMEMİŞ `personnel` listesiyle (aktive personel + bekleyen
 * davetler BİRLİKTE) çağırıyordu — bir davet satırı `firstName`/`lastName` taşır ve
 * `tcKimlikNo` HER ZAMAN `null`'dır (§13.6), yani ad-yedek dalı bir davetle de eşleşirdi: henüz
 * etkinleşmemiş bir kişi sessizce "kayıtlı" sayılıyor, `KAYIT DIŞI` rozeti VE "Yeni kişi ekle"
 * kaçış yolu ikisi de BASTIRILIYORDU — Kişi seçici ise `kind === 'Personnel'` dışını hiç
 * LİSTELEMEDİĞİ için `Onayla` açıklamasız kilitli kalıyordu.
 *
 * Üç sonuç:
 *   - `registered`   — `kind === 'Personnel'` satırları arasında eşleşme VAR (Kişi seçicinin
 *     GÖSTERDİĞİ AYNI küme).
 *   - `invited`      — aktive personelde eşleşme YOK ama `kind === 'Invitation'` VE
 *     `status === 'Pending'` bir satırla ad token'ları KESİŞİYOR (TC her zaman `null` olduğundan
 *     davetlerde yalnız ad karşılaştırılır) — "hiç duyulmamış biri" değil, "davet edildi ama
 *     henüz etkinleşmedi" ayrı bir durumdur; kullanıcı bunu bilmeli (bu belge o kişi
 *     ETKİNLEŞENE kadar dosyalanamaz).
 *   - `unregistered` — hiçbir eşleşme yok (klasik KAYIT DIŞI, "Yeni kişi ekle" burada sunulur).
 */
export function personnelRegistrationStatus(
  identity: ExtractedIdentity,
  personnel: ContractorPersonnelDto[],
): PersonnelRegistrationStatus {
  const activePersonnel = personnel.filter((p) => p.kind === 'Personnel');
  if (isRegisteredPersonnel(identity, activePersonnel)) {
    return 'registered';
  }
  if (identity.tcKimlikNo) {
    // Davetlerde `tcKimlikNo` hiçbir zaman dolmaz (§13.6) — TC ile bir davete karşı eşleşme
    // yapılacak bir sinyal yok, doğrudan KAYIT DIŞI.
    return 'unregistered';
  }
  if (identity.fullName) {
    const tokens = nameTokens(identity.fullName);
    if (tokens.size > 0) {
      const pendingInvitations = personnel.filter(
        (p) => p.kind === 'Invitation' && p.status === 'Pending',
      );
      const invited = pendingInvitations.some((p) => {
        const personTokens = nameTokens(`${p.firstName} ${p.lastName}`);
        for (const token of tokens) {
          if (personTokens.has(token)) {
            return true;
          }
        }
        return false;
      });
      if (invited) {
        return 'invited';
      }
    }
  }
  return 'unregistered';
}

export type PersonMismatchKind = 'tc' | 'name' | 'tcNameConflict';

/**
 * TC EŞLEŞTİĞİNDE kullanılan, `namesConflict`ten KASITLI OLARAK DAHA SIKI ad karşılaştırması.
 * `namesConflict` (bkz. `nameMatch.ts`) "en az bir ortak token" ile YETİNİR çünkü o dal TC YOKKEN
 * tek sinyaldir — YANLIŞ KİŞİYİ suçlama riski var, az-tetikleme güvenli yöndür. Burada TC ZATEN
 * doğru kişiyi sağlama toplamıyla doğruladı; soru "doğru kişi mi" değil "belge bu kişiyi doğru mu
 * yazmış" — o yüzden "en az bir ortak token" YETMEZ: `ESMAN BAYDUR` ile `OSMAN BAYDUR` tam olarak
 * `BAYDUR`u paylaşır ve `namesConflict` bunu "çakışma yok" sayardı (asıl kırık senaryo buydu).
 * Token KÜMELERİ EŞİT (ya da biri diğerinin ALT KÜMESİ) değilse çakışma sayılır.
 *
 * İki tasarım kararı:
 *   - Sıra ÖNEMSİZ (Set karşılaştırması): belge türüne göre alan `ad soyad` ya da `soyad ad`
 *     sırasıyla gelebilir (bkz. dosya üstü not §"TC ÖNCE, ad YEDEK") — sıra farkı bir OKUMA
 *     HATASI değil, alan biçimidir; çakışma sayılmamalı.
 *   - FAZLADAN token (`OSMAN ALİ BAYDUR` belgede, kayıtta `OSMAN BAYDUR`) alt küme sayılır,
 *     ÇAKIŞMA DEĞİL: ikinci ad/baba adı gibi meşru farklar Türk kimlik belgelerinde YAYGINDIR
 *     ve TC zaten kişiyi doğruladığı için bu fazlalık "yanlış kişi" ya da "kötü okuma" belirtisi
 *     değildir. Bunu da çakışma saymak, tam olarak `namesConflict`in header'ının kaçınmaya
 *     çalıştığı şeyi burada yeniden üretirdi: belgelerin büyük kısmında hatasızca tetiklenen bir
 *     uyarı, taşeronun uyarıları GÖRMEZDEN GELMEYİ öğrenmesine yol açar. Gerçek okuma hataları
 *     (bu dosyanın yazılma sebebi olan olay dahil) VAR OLAN bir token'ı BOZAR (`OSMAN`→`ESMAN`),
 *     inandırıcı YENİ bir isim EKLEMEZ — alt küme olmayan fark bu bozulmayı zaten yakalar.
 */
function tcNameConflict(readName: string, personName: string): boolean {
  const readTokens = nameTokens(readName);
  const personTokens = nameTokens(personName);
  if (readTokens.size === 0 || personTokens.size === 0) {
    return false;
  }
  let readIsSubsetOfPerson = true;
  for (const token of readTokens) {
    if (!personTokens.has(token)) {
      readIsSubsetOfPerson = false;
      break;
    }
  }
  if (readIsSubsetOfPerson) {
    return false;
  }
  let personIsSubsetOfRead = true;
  for (const token of personTokens) {
    if (!readTokens.has(token)) {
      personIsSubsetOfRead = false;
      break;
    }
  }
  return !personIsSubsetOfRead;
}

/**
 * Seçili kişi ↔ belgeden okunan kimlik uyuşmazlığı (AC8). TC ikisi de doluyken KARŞILAŞTIRILIR
 * (güçlü sinyal, `selected.tcKimlikNo` boşsa karşılaştırılamaz → ada düşülür); aksi hâlde ada
 * düşülür (`namesConflict`, zayıf sinyal). Onayı ASLA ENGELLEMEZ — çağıran yalnız GÖSTERİR.
 *
 * **REVİZE (bu dilim) — eşleşen bir TC artık ad kontrolünü BASTIRMAZ.** Gerçek bir olay bunu
 * gösterdi: bir kimlik kartında `ad` alanı "ESMAN" okundu, doğrusu "OSMAN"dı (hologram harfi
 * bozmuş) — o alanın kendi güven skoru 0.86'ydı, kalan tüm alanlar 0.96-0.99. T.C. numarası
 * doğru kişiyle eşleşiyordu, eski kod bunu görünce hemen `null` dönüyordu — aşağıdaki ad
 * kontrolüne HİÇ ULAŞILMIYORDU, taşeron hiçbir uyarı görmeden yanlış adı onayladı. Şimdi: TC
 * eşleşse BİLE ad ayrıca denetlenir — ama `namesConflict` İLE DEĞİL: o "en az bir ortak token"
 * kuralı bu senaryonun TAM OLARAK yakalayamadığı türdendi (`ESMAN BAYDUR`/`OSMAN BAYDUR` ortak
 * `BAYDUR`u paylaşır, `namesConflict` "çakışma yok" derdi). Bunun yerine yukarıdaki DAHA SIKI
 * `tcNameConflict` yardımcısı kullanılır (gerekçe orada). TC FARKLIYSA davranış AYNEN korunur
 * (`'tc'`, sert uyuşmazlık, `namesConflict`e hiç bakılmaz — o dal DEĞİŞMEDİ). TC EŞLEŞİP ad
 * çakışıyorsa bu `'tc'` DEĞİL, ayrı ve daha YUMUŞAK bir `'tcNameConflict'` döner: T.C. sağlama
 * toplamıyla doğrulanan güçlü bir sinyal zaten doğru kişiyi işaret ediyorken adın çakışması
 * çoğunlukla YANLIŞ KİŞİ değil bir OKUMA HATASININ imzasıdır — metin bunu suçlayıcı değil
 * "belgeyi kontrol edin" tonunda söyler (bkz. `analysis.person.mismatch.tcNameConflict*` /
 * `analysis.review.confirm.mismatch.tcNameConflict`).
 */
export function personMismatch(
  identity: ExtractedIdentity,
  selected: ContractorPersonnelDto,
): PersonMismatchKind | null {
  if (identity.tcKimlikNo && selected.tcKimlikNo) {
    if (identity.tcKimlikNo !== selected.tcKimlikNo) {
      return 'tc';
    }
    if (
      identity.fullName &&
      tcNameConflict(identity.fullName, `${selected.firstName} ${selected.lastName}`)
    ) {
      return 'tcNameConflict';
    }
    return null;
  }
  if (identity.fullName) {
    return namesConflict(identity.fullName, `${selected.firstName} ${selected.lastName}`)
      ? 'name'
      : null;
  }
  return null;
}
